/**
 * Resolves the company (tenant) the current session is acting as.
 *
 * An admin may be granted access to other companies in the agency and switch
 * between them; switching re-mints the app JWT with a different `admin_id`
 * claim. The claim is therefore the single source of truth for "which company
 * am I in right now" — deliberately not a separate localStorage key, which
 * could drift out of sync with the token when a grant is revoked and leave the
 * client reading a company the token no longer authorises.
 *
 * Signature verification is intentionally skipped: this value only drives
 * client-side query filters and display. The server re-derives the tenant from
 * the verified token on every edge-function call.
 *
 * Dependency-free so `utils/getAdminId.ts` and `lib/tenantStorage.ts` can both
 * use it without an import cycle.
 */

export const AUTH_TOKEN_KEY = 'auth_token'

let cachedToken: string | null = null
let cachedAdminId: string | null = null

/** Read the `admin_id` claim from a JWT without verifying the signature. */
function readAdminIdClaim(token: string): string | null {
	try {
		const part = token.split('.')[1]
		if (!part) return null
		const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
		const payload = JSON.parse(json) as { admin_id?: unknown }
		return typeof payload.admin_id === 'string' && payload.admin_id ? payload.admin_id : null
	} catch {
		return null
	}
}

/**
 * The company id this session is currently acting as, or null when there is no
 * usable token. Callers fall back to their role-derived tenant in that case.
 */
export function getActiveCompanyId(): string | null {
	try {
		const token = localStorage.getItem(AUTH_TOKEN_KEY)
		if (!token) {
			cachedToken = null
			cachedAdminId = null
			return null
		}
		if (token !== cachedToken) {
			cachedToken = token
			cachedAdminId = readAdminIdClaim(token)
		}
		return cachedAdminId
	} catch {
		return null
	}
}

/** Drop the memoised claim so the next read re-parses the stored token. */
export function resetActiveCompanyCache(): void {
	cachedToken = null
	cachedAdminId = null
}
